import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Download, Snowflake, ChevronUp, ChevronDown } from 'lucide-react';
import {
    ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar, Cell,
    XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import axiosInstance from '../utils/axiosConfig';
import './TicketsReportes.css';

// ============================================================
// Helpers
// ============================================================

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PRESETS = [
    { key: 'mes', label: 'Este mes', range: () => { const h = new Date(); return [new Date(h.getFullYear(), h.getMonth(), 1), h]; } },
    { key: '30', label: '30 días', range: () => { const h = new Date(); const d = new Date(h); d.setDate(h.getDate() - 29); return [d, h]; } },
    { key: '90', label: '90 días', range: () => { const h = new Date(); const d = new Date(h); d.setDate(h.getDate() - 89); return [d, h]; } },
    { key: 'anio', label: 'Este año', range: () => { const h = new Date(); return [new Date(h.getFullYear(), 0, 1), h]; } },
];

const fmtH = (h) => (h === null || h === undefined ? '—' : `${h} h`);
const fmtPct = (p) => (p === null || p === undefined ? '—' : `${p}%`);
const slaColor = (p) => (p === null || p === undefined ? '#94a3b8' : p >= 80 ? '#10b981' : p >= 50 ? '#f59e0b' : '#ef4444');

const fmtDuration = (seconds) => {
    const s = Math.abs(seconds);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d} d ${h} h`;
    if (h > 0) return `${h} h ${pad(m)} m`;
    return `${m} min`;
};

const fmtShortDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });
};

const fmtBucket = (iso, gran) => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (gran === 'mes') return date.toLocaleDateString('es-VE', { month: 'short', year: '2-digit' });
    return date.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });
};

const PRIO_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
const PRIO_COLOR = { baja: '#64748b', media: '#3b82f6', alta: '#f59e0b', critica: '#ef4444' };

// ============================================================
// Componente
// ============================================================

const TicketsReportes = ({ categorias, onDrill }) => {
    const [preset, setPreset] = useState('30');
    const [rango, setRango] = useState(() => PRESETS[1].range().map(toISO));
    const [custom, setCustom] = useState({ desde: '', hasta: '' });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [sort, setSort] = useState({ key: 'cerrados', dir: 'desc' });
    const reqId = useRef(0);

    const [desde, hasta] = rango;

    const fetchData = useCallback(async () => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/tickets/reports', { params: { desde, hasta } });
            if (id === reqId.current) setData(res.data);
        } catch (err) {
            if (id === reqId.current) setError(err.response?.data?.error || 'No se pudo cargar el tablero');
        } finally {
            if (id === reqId.current) setLoading(false);
        }
    }, [desde, hasta]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const applyPreset = (key) => {
        const p = PRESETS.find(x => x.key === key);
        if (!p) return;
        setPreset(key);
        setRango(p.range().map(toISO));
    };

    const applyCustom = (next) => {
        setCustom(next);
        if (next.desde && next.hasta && next.desde <= next.hasta) {
            setPreset('custom');
            setRango([next.desde, next.hasta]);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await axiosInstance.get('/api/tickets/reports/export.xlsx', {
                params: { desde, hasta }, responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `soporte-ti_${desde}_${hasta}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exportando:', err);
        } finally {
            setExporting(false);
        }
    };

    const drill = (filters) => onDrill && onDrill(filters);
    const periodo = { desde, hasta };

    const tecnicosOrdenados = useMemo(() => {
        if (!data) return [];
        const rows = [...data.tecnicos];
        const dir = sort.dir === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
            if (sort.key === 'nombre') return a.nombre.localeCompare(b.nombre) * dir;
            const av = a[sort.key], bv = b[sort.key];
            if (av === null || av === undefined) return 1;
            if (bv === null || bv === undefined) return -1;
            return (av - bv) * dir;
        });
        return rows;
    }, [data, sort]);

    const toggleSort = (key) => {
        setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'nombre' ? 'asc' : 'desc' }));
    };

    const catInfo = (key) => (categorias || []).find(c => c.key === key) || { label: key, color: '#94a3b8' };
    const catColor = (key) => catInfo(key).color;

    const maxArea = data ? Math.max(...data.por_area.map(a => a.total), 1) : 1;
    const sinPeriodo = data && data.resumen.creados === 0 && data.resumen.cerrados === 0;

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------

    const SortIcon = ({ col }) => {
        if (sort.key !== col) return null;
        return sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
    };

    const renderBacklog = () => {
        const b = data.backlog;
        const cards = [
            { key: 'abiertos', label: 'Abiertos', value: b.abiertos, drill: { status: 'abierto' } },
            { key: 'en_progreso', label: 'En progreso', value: b.en_progreso, drill: { status: 'en_progreso' } },
            { key: 'sin_asignar', label: 'Sin asignar', value: b.sin_asignar, drill: { sin_asignar: true } },
            { key: 'congelados', label: 'Congelados', value: b.congelados, drill: { congelado: true } },
            { key: 'por_vencer', label: 'Por vencer', value: b.por_vencer, tone: 'warn', drill: { sla_por_vencer: true } },
            { key: 'vencidos', label: 'SLA vencido', value: b.vencidos, tone: 'danger', drill: { sla_vencido: true } },
        ];
        return (
            <section className="tkr-block">
                <div className="tkr-block-head">
                    <h3>Backlog ahora</h3>
                    <span className="tkr-block-note">{b.total === 0 ? 'Sin tickets pendientes' : `${b.total} ticket${b.total === 1 ? '' : 's'} vivo${b.total === 1 ? '' : 's'}`}</span>
                </div>
                <div className="tkr-counters">
                    {cards.map(c => (
                        <button key={c.key}
                            className={`tkr-counter ${c.tone && c.value > 0 ? c.tone : ''} ${c.value === 0 ? 'zero' : ''}`}
                            onClick={() => drill(c.drill)} title="Ver estos tickets">
                            <span className="tkr-counter-num">{c.value}</span>
                            <span className="tkr-counter-label">{c.label}</span>
                        </button>
                    ))}
                </div>
                {b.tickets.length > 0 && (
                    <div className="tkr-table-wrap">
                        <table className="tkr-table">
                            <thead>
                                <tr>
                                    <th className="num">#</th>
                                    <th>Título</th>
                                    <th>Técnico</th>
                                    <th>Prioridad</th>
                                    <th className="num">Edad</th>
                                    <th className="num">SLA restante</th>
                                </tr>
                            </thead>
                            <tbody>
                                {b.tickets.map(t => {
                                    const age = (Date.now() - new Date(t.created_at).getTime()) / 1000;
                                    const vencido = !t.frozen && t.sla_restante_seconds < 0;
                                    const porVencer = !t.frozen && !vencido && t.sla_restante_seconds < t.sla_hours * 3600 * 0.25;
                                    return (
                                        <tr key={t.id} className="tkr-row-link" onClick={() => drill({ ticket: t.id })}>
                                            <td className="num muted">#{t.id}</td>
                                            <td className="tkr-td-title">{t.titulo}</td>
                                            <td className={t.assignee_name ? '' : 'muted'}>{t.assignee_name || 'Sin asignar'}</td>
                                            <td><span className="tkr-prio" style={{ color: PRIO_COLOR[t.prioridad] }}>{PRIO_LABEL[t.prioridad] || t.prioridad}</span></td>
                                            <td className="num">{fmtDuration(age)}</td>
                                            <td className={`num ${vencido ? 'danger' : porVencer ? 'warn' : ''}`}>
                                                {t.frozen
                                                    ? <span className="tkr-frozen"><Snowflake size={12} /> Congelado</span>
                                                    : vencido ? `−${fmtDuration(t.sla_restante_seconds)}` : fmtDuration(t.sla_restante_seconds)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {b.total > b.tickets.length && (
                            <div className="tkr-table-more">y {b.total - b.tickets.length} más</div>
                        )}
                    </div>
                )}
            </section>
        );
    };

    const renderTecnicos = () => (
        <section className="tkr-block">
            <div className="tkr-block-head">
                <h3>Técnicos</h3>
                <span className="tkr-block-note">Cerrados y tiempos del período; abiertos es ahora</span>
            </div>
            {sinPeriodo && data.tecnicos.every(t => t.abiertos === 0) ? (
                <p className="tkr-empty">Sin tickets en este período</p>
            ) : (
                <div className="tkr-table-wrap">
                    <table className="tkr-table tkr-table-sortable">
                        <thead>
                            <tr>
                                <th onClick={() => toggleSort('nombre')}>Técnico <SortIcon col="nombre" /></th>
                                <th className="num" onClick={() => toggleSort('cerrados')}>Cerrados <SortIcon col="cerrados" /></th>
                                <th className="num" onClick={() => toggleSort('abiertos')}>Abiertos <SortIcon col="abiertos" /></th>
                                <th className="num" onClick={() => toggleSort('avg_resolucion_h')}>Resolución <SortIcon col="avg_resolucion_h" /></th>
                                <th className="num" onClick={() => toggleSort('avg_primera_respuesta_h')}>1ª respuesta <SortIcon col="avg_primera_respuesta_h" /></th>
                                <th className="num" onClick={() => toggleSort('sla_pct')}>SLA <SortIcon col="sla_pct" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tecnicosOrdenados.map(t => (
                                <tr key={t.user_id ?? 'none'} className="tkr-row-link"
                                    onClick={() => drill(t.user_id === null
                                        ? { sin_asignar: true, ...periodo }
                                        : { assigned_to: t.user_id, assignee_name: t.nombre, ...periodo })}>
                                    <td className={t.user_id === null ? 'muted' : ''}>{t.nombre}</td>
                                    <td className="num strong">{t.cerrados}</td>
                                    <td className="num">{t.abiertos}</td>
                                    <td className="num">{fmtH(t.avg_resolucion_h)}</td>
                                    <td className="num">{fmtH(t.avg_primera_respuesta_h)}</td>
                                    <td className="num"><span className="tkr-sla" style={{ color: slaColor(t.sla_pct) }}>{fmtPct(t.sla_pct)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );

    const renderOrigen = () => {
        const r = data.resumen;
        const gran = data.tendencia.granularidad;
        const tendencia = data.tendencia.puntos.map(p => ({ ...p, label: fmtBucket(p.fecha, gran) }));
        return (
            <section className="tkr-block">
                <div className="tkr-block-head">
                    <h3>Origen del volumen</h3>
                    <span className="tkr-block-note">
                        {r.creados} creado{r.creados === 1 ? '' : 's'}, {r.cerrados} cerrado{r.cerrados === 1 ? '' : 's'}
                        {r.avg_resolucion_h !== null && ` · resolución ${r.avg_resolucion_h} h`}
                        {r.sla_pct !== null && ` · SLA ${r.sla_pct}%`}
                    </span>
                </div>
                {sinPeriodo ? (
                    <p className="tkr-empty">Sin tickets en este período</p>
                ) : (
                    <>
                        <div className="tkr-origen-grid">
                            <div className="tkr-card">
                                <h4>Por área solicitante</h4>
                                {data.por_area.map(a => (
                                    <button key={a.area_id ?? 'otras'} className="tkr-bar-row" disabled={a.area_id === null}
                                        onClick={() => drill({ area_id: a.area_id, area_name: a.nombre, ...periodo })}>
                                        <span className="tkr-bar-label">{a.nombre}</span>
                                        <span className="tkr-bar-track"><span className="tkr-bar-fill" style={{ width: `${(a.total / maxArea) * 100}%` }} /></span>
                                        <span className="tkr-bar-val">{a.total}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="tkr-card">
                                <h4>Por categoría</h4>
                                <div className="tkr-chart tkr-chart-cat">
                                    <ResponsiveContainer width="100%" height={Math.max(160, data.por_categoria.length * 28)}>
                                        <BarChart data={data.por_categoria} layout="vertical" margin={{ top: 0, right: 28, bottom: 0, left: 0 }} barCategoryGap={6}>
                                            <XAxis type="number" hide allowDecimals={false} />
                                            <YAxis type="category" dataKey="categoria" width={118} tickLine={false} axisLine={false}
                                                tick={{ fontSize: 12, fill: '#475569' }} tickFormatter={(k) => catInfo(k).label} />
                                            <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v) => [v, 'Tickets']} labelFormatter={(k) => catInfo(k).label}
                                                contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
                                            <Bar dataKey="total" radius={[0, 6, 6, 0]} cursor="pointer" label={{ position: 'right', fontSize: 12, fill: '#475569' }}
                                                onClick={(d) => drill({ categoria: d.categoria, ...periodo })}>
                                                {data.por_categoria.map(c => <Cell key={c.categoria} fill={catColor(c.categoria)} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                        <div className="tkr-card">
                            <div className="tkr-card-head">
                                <h4>Creados y cerrados</h4>
                                <div className="tkr-legend">
                                    <span><i style={{ background: '#3b82f6' }} /> Creados</span>
                                    <span><i style={{ background: '#10b981' }} /> Cerrados</span>
                                    <span className="tkr-legend-gran">por {gran === 'dia' ? 'día' : gran}</span>
                                </div>
                            </div>
                            <div className="tkr-chart">
                                <ResponsiveContainer width="100%" height={220}>
                                    <ComposedChart data={tendencia} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                                        <defs>
                                            <linearGradient id="tkrCreados" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.18} />
                                                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} minTickGap={24} />
                                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                        <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                                            formatter={(v, name) => [v, name === 'creados' ? 'Creados' : 'Cerrados']} />
                                        <Area type="monotone" dataKey="creados" stroke="#3b82f6" strokeWidth={2} fill="url(#tkrCreados)" dot={false} activeDot={{ r: 4 }} />
                                        <Line type="monotone" dataKey="cerrados" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </>
                )}
            </section>
        );
    };

    const renderSkeleton = () => (
        <div className="tkr-skeleton skeleton">
            <div className="tkr-sk tkr-sk-counters" />
            <div className="tkr-sk tkr-sk-table" />
            <div className="tkr-sk tkr-sk-table" />
        </div>
    );

    return (
        <div className="tkr">
            <div className="tkr-toolbar">
                <div className="tkr-presets" role="tablist">
                    {PRESETS.map(p => (
                        <button key={p.key} className={`tkr-preset ${preset === p.key ? 'active' : ''}`} onClick={() => applyPreset(p.key)}>
                            {p.label}
                        </button>
                    ))}
                    <div className={`tkr-custom ${preset === 'custom' ? 'active' : ''}`}>
                        <input type="date" value={custom.desde} max={custom.hasta || undefined}
                            onChange={e => applyCustom({ ...custom, desde: e.target.value })} aria-label="Desde" />
                        <span>a</span>
                        <input type="date" value={custom.hasta} min={custom.desde || undefined}
                            onChange={e => applyCustom({ ...custom, hasta: e.target.value })} aria-label="Hasta" />
                    </div>
                </div>
                <div className="tkr-toolbar-right">
                    <span className="tkr-range">{fmtShortDate(desde)} – {fmtShortDate(hasta)}</span>
                    <button className="tkr-export" onClick={handleExport} disabled={exporting || !data}>
                        <Download size={15} /> {exporting ? 'Generando...' : 'Exportar Excel'}
                    </button>
                </div>
            </div>

            {error && <div className="tkr-error">{error}</div>}

            {!data && loading && renderSkeleton()}

            {data && (
                <div className={`tkr-blocks ${loading ? 'dim' : ''}`}>
                    {renderBacklog()}
                    {renderTecnicos()}
                    {renderOrigen()}
                </div>
            )}
        </div>
    );
};

export default TicketsReportes;
